import { value } from '@takeshape/util';
import { getAuthInfoCacheExpirationForClientCredentials, getAuthInfoRequestConfigForClientCredentials, getAuthInfoRequestResultsForClientCredentials } from "../util/auth-info.js";
import { hasEveryKey } from "../util/has.js";
import { fixObfuscatedProp } from "../util/props.js";
const title = 'Petfinder';
const serviceProvider = {
    name: 'petfinder',
    title,
    description: 'Petfinder V2 API',
    categories: ['fun'],
    links: [{ title: 'Homepage', url: 'https://www.petfinder.com/' }],
    serviceType: 'rest',
    createId: value('petfinder'),
    getFormSchema: value({
        type: 'object',
        properties: {
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '^[0-9a-z_-]+$',
                default: 'petfinder'
            },
            title: {
                title: 'Name',
                type: 'string',
                default: 'Petfinder'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'Petfinder'
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    clientId: {
                        title: 'Client ID',
                        type: 'string',
                        '@sensitive': true
                    },
                    clientSecret: {
                        title: 'Client Secret',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['clientId', 'clientSecret']
            }
        },
        required: ['title', 'id', 'namespace', 'authentication']
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'A unique, url-safe identifier for this service.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            authentication: {
                order: ['clientId', 'clientSecret'],
                properties: {
                    clientId: {
                        widget: 'sensitiveField'
                    },
                    clientSecret: {
                        widget: 'sensitiveField'
                    }
                }
            }
        }
    }),
    getConfig(formData) {
        const config = {
            title: formData.title ?? 'Petfinder',
            namespace: formData.namespace ?? 'Petfinder',
            provider: 'petfinder',
            serviceType: 'rest',
            authenticationType: 'oauth2',
            options: {
                endpoint: 'https://api.petfinder.com/v2/'
            }
        };
        const { authentication } = formData;
        if (!authentication) {
            return config;
        }
        config.authentication = {
            type: 'oauth2',
            grantType: 'clientCredentials',
            authorizationUrl: 'https://api.petfinder.com/v2/oauth2/token',
            accessTokenUrl: 'https://api.petfinder.com/v2/oauth2/token',
            clientId: fixObfuscatedProp(authentication.clientId),
            clientSecret: fixObfuscatedProp(authentication.clientSecret)
        };
        return config;
    },
    getAuthInfoCacheExpiration: getAuthInfoCacheExpirationForClientCredentials(title),
    getAuthInfoRequestConfig: getAuthInfoRequestConfigForClientCredentials(title),
    getAuthInfoResult: getAuthInfoRequestResultsForClientCredentials(title),
    isCompleteConfig: hasEveryKey(['authentication', 'options.endpoint'])
};
export default serviceProvider;
